import Cookies from "universal-cookie";

const cookies = new Cookies();
const BASE = process.env.AI_SERVER_URL || "";
const LOOPBACK_HOSTS = new Set(["localhost", "127.0.0.1", "[::1]"]);

const clientError = ({
  status,
  errorCode,
  message,
  retryable = false,
  draft,
  reason,
}) => ({
  status,
  code: errorCode,
  errorCode,
  message,
  retryable,
  ...(draft && typeof draft === "object" && !Array.isArray(draft)
    ? { draft }
    : {}),
  ...(typeof reason === "string" ? { reason } : {}),
});

const ACTION_ERROR_MESSAGES = Object.freeze({
  financial_source_protocol_unavailable:
    "A sincronização financeira segura desta obra ainda não está disponível. Esta tentativa não consumiu sua cota.",
  financial_source_refresh_in_progress:
    "Os dados financeiros estão sendo atualizados. Tente novamente em instantes. Esta tentativa não consumiu sua cota.",
  financial_source_coordination_conflict:
    "Os dados financeiros mudaram durante a leitura. Tente novamente. Esta tentativa não consumiu sua cota.",
  ai_provider_unavailable:
    "O provedor de IA está temporariamente indisponível. Esta tentativa não consumiu sua cota.",
  ai_provider_invalid_output:
    "A IA não retornou uma proposta segura. Esta tentativa não consumiu sua cota.",
});

const safeMessage = (status, errorCode) => {
  if (Object.hasOwn(ACTION_ERROR_MESSAGES, errorCode)) {
    return ACTION_ERROR_MESSAGES[errorCode];
  }
  if (status === 401) return "Faça login novamente para usar a IA.";
  if (status === 403)
    return "As análises com IA não estão habilitadas nesta obra.";
  if (status === 404) return "A obra ou a análise não foi encontrada.";
  if (status === 409)
    return "Os dados desta análise mudaram. Recarregue e tente novamente.";
  if (status === 410) return "O rascunho expirou. Monte uma nova proposta.";
  if (status === 422)
    return "Não foi possível determinar o contexto desta análise.";
  if (status === 429)
    return "A cota de análises desta obra terminou neste período.";
  return "A IA está temporariamente indisponível.";
};

const retryableError = (status, errorCode) => {
  if (errorCode === "financial_source_protocol_unavailable") return false;
  if (
    errorCode === "financial_source_refresh_in_progress" ||
    errorCode === "financial_source_coordination_conflict" ||
    errorCode === "ai_provider_unavailable" ||
    errorCode === "ai_provider_invalid_output"
  ) {
    return true;
  }
  return status >= 500;
};

const configuredBase = () => {
  if (!BASE) {
    throw clientError({
      status: 0,
      errorCode: "configuration_error",
      message: "Análises com IA não configuradas.",
    });
  }
  try {
    const base = new URL(BASE);
    if (
      (base.protocol !== "https:" && !(
        base.protocol === "http:"
        && process.env.NODE_ENV === "development"
        && LOOPBACK_HOSTS.has(base.hostname.toLowerCase())
      )) ||
      base.username ||
      base.password
    ) {
      throw new Error();
    }
    return base;
  } catch {
    throw clientError({
      status: 0,
      errorCode: "configuration_error",
      message: "Análises com IA não configuradas.",
    });
  }
};

const actionFetch = async (
  path,
  { method = "GET", body, idempotencyKey, signal } = {},
) => {
  const base = configuredBase();
  const basePath = base.pathname.replace(/\/+$/, "");
  const url = new URL(`${basePath}/${path}`, `${base.origin}/`);
  if (url.origin !== base.origin) {
    throw clientError({
      status: 0,
      errorCode: "configuration_error",
      message: "Análises com IA não configuradas.",
    });
  }
  const { token } = cookies.get("userId") || {};
  if (typeof token !== "string" || !token) {
    throw clientError({
      status: 401,
      errorCode: "authentication_required",
      message: safeMessage(401),
    });
  }
  const headers = { Authorization: `Bearer ${token}` };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (idempotencyKey) headers["Idempotency-Key"] = idempotencyKey;

  let response;
  try {
    response = await fetch(url.toString(), {
      method,
      headers,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(signal ? { signal } : {}),
    });
  } catch (error) {
    if (error?.name === "AbortError") {
      throw clientError({
        status: 0,
        errorCode: "request_cancelled",
        message: "Análise cancelada.",
      });
    }
    throw clientError({
      status: 0,
      errorCode: "network_error",
      message: "Não foi possível comunicar com a IA.",
      retryable: true,
    });
  }

  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const errorCode =
      typeof data?.errorCode === "string" ? data.errorCode : "request_failed";
    throw clientError({
      status: response.status,
      errorCode,
      message: safeMessage(response.status, errorCode),
      retryable: retryableError(response.status, errorCode),
      draft: data?.draft,
      reason: data?.reason,
    });
  }
  return data;
};

const workPath = (workId) =>
  `works/${encodeURIComponent(String(workId))}/ai/action-drafts`;
const draftPath = (workId, draftId) =>
  `${workPath(workId)}/${encodeURIComponent(String(draftId))}`;

export const createActionDraft = (
  workId,
  { module, prompt },
  { idempotencyKey, signal } = {},
) =>
  actionFetch(workPath(workId), {
    method: "POST",
    body: { module, prompt },
    idempotencyKey,
    signal,
  });

export const fetchActionDraft = (workId, draftId, options = {}) =>
  actionFetch(draftPath(workId, draftId), options);

export const updateActionDraft = (workId, draftId, changes, { signal } = {}) =>
  actionFetch(draftPath(workId, draftId), {
    method: "PATCH",
    body: changes,
    signal,
  });

export const confirmActionDraft = (
  workId,
  draftId,
  { expectedVersion },
  { idempotencyKey, signal } = {},
) =>
  actionFetch(`${draftPath(workId, draftId)}/confirm`, {
    method: "POST",
    body: { expectedVersion },
    idempotencyKey,
    signal,
  });

export default {
  createActionDraft,
  fetchActionDraft,
  updateActionDraft,
  confirmActionDraft,
};
